<div class="push-page">
  <div class="topline">
    <div>
      <h2>Push Notifications</h2>
      <p>Compose, preview, and send app push messages.</p>
    </div>
    <span class="status-pill">Superadmin</span>
  </div>

  <form [formGroup]="form" class="composer">
    <section class="panel audience-panel">
      <div class="section-title">
        <mat-icon>near_me</mat-icon>
        <div>
          <h3>Destination</h3>
          <p>Choose who should receive this push.</p>
        </div>
      </div>

      <div class="destination-grid">
        @for (option of destinationTypes; track option) {
          <button
            type="button"
            class="destination-card"
            [class.selected]="selectedDestinationType === option.value"
            (click)="form.patchValue({ destinationType: option.value })">
            <mat-icon>{{ option.icon }}</mat-icon>
            <span class="destination-label">{{ option.label }}</span>
          </button>
        }
      </div>

      <div class="destination-detail">
        @if (selectedDestinationType === 'user') {
          <mat-form-field appearance="fill" floatLabel="always">
            <mat-label>User ID</mat-label>
            <input matInput formControlName="userId">
            <mat-icon matPrefix>person</mat-icon>
          </mat-form-field>
        }

        @if (selectedDestinationType === 'brand') {
          <mat-form-field appearance="fill" floatLabel="always">
            <mat-label>Brand</mat-label>
            <mat-select formControlName="destinationValue" panelClass="member-addedit-select-search-panel">
              <mat-option [disabled]="true">
                <ngx-mat-select-search
                  [formControl]="destinationSearchControl"
                  noEntriesFoundLabel="No matches"
                  placeholderLabel="Search..."
                ></ngx-mat-select-search>
              </mat-option>
              @for (destination of filteredBrandDestinations; track brandTrackBy($index, destination)) {
                <mat-option [value]="destination.topic">
                  {{ destination.name }}
                </mat-option>
              }
            </mat-select>
            <mat-icon matPrefix>sell</mat-icon>
          </mat-form-field>
        }

        @if (selectedDestinationType === 'existingUser' || selectedDestinationType === 'company' || selectedDestinationType === 'team') {
          <mat-form-field appearance="fill" floatLabel="always">
            <mat-label>{{ selectedDestinationOption?.label }}</mat-label>
            <mat-select formControlName="destinationValue" panelClass="member-addedit-select-search-panel">
              <mat-option [disabled]="true">
                <ngx-mat-select-search
                  [formControl]="destinationSearchControl"
                  noEntriesFoundLabel="No matches"
                  placeholderLabel="Search..."
                ></ngx-mat-select-search>
              </mat-option>
              @for (destination of filteredSelectableDestinations; track destinationTrackBy($index, destination)) {
                <mat-option [value]="destination.id">
                  {{ destination.name }}@if (selectedDestinationType !== 'existingUser' && destination.email) {
                  <span class="option-secondary"> {{ destination.email }}</span>
                }
              </mat-option>
            }
            @if (selectedDestinationType === 'existingUser' && !existingUserSearchReady && filteredSelectableDestinations.length === 0) {
              <mat-option [disabled]="true">
                Enter at least {{ existingUserSearchMinLength }} characters
              </mat-option>
            }
            @if (selectedDestinationType === 'existingUser' && existingUserSearchReady && loadingDestinations) {
              <mat-option [disabled]="true">
                Searching users...
              </mat-option>
            }
            @if (selectedDestinationType === 'existingUser' && existingUserSearchReady && !loadingDestinations && filteredSelectableDestinations.length === 0) {
              <mat-option [disabled]="true">
                No users found
              </mat-option>
            }
          </mat-select>
          <mat-icon matPrefix>{{ selectedDestinationOption?.icon }}</mat-icon>
        </mat-form-field>
      }

      @if (selectedDestinationType === 'marketingGroup') {
        <mat-form-field appearance="fill" floatLabel="always">
          <mat-label>Marketing group</mat-label>
          <mat-select formControlName="destinationValue" panelClass="member-addedit-select-search-panel">
            <mat-option [disabled]="true">
              <ngx-mat-select-search
                [formControl]="destinationSearchControl"
                noEntriesFoundLabel="No matches"
                placeholderLabel="Search..."
              ></ngx-mat-select-search>
            </mat-option>
            @for (group of filteredMarketingGroups; track destinationTrackBy($index, group)) {
              <mat-option [value]="group.id">
                {{ group.name }}<span class="option-secondary"> {{ getTypeLabel(group.memberType) }}</span>
              </mat-option>
            }
          </mat-select>
          <mat-icon matPrefix>group_work</mat-icon>
        </mat-form-field>
      }

      @if (loadingDestinations) {
        <div class="loading">
          <mat-spinner diameter="20"></mat-spinner>
          Loading destinations
        </div>
      }

      @if (destinationLoadError) {
        <app-inlinemessage level="error" [message]="destinationLoadError"></app-inlinemessage>
      }

      @if (lastDeliverySummary) {
        <div
          class="delivery-note"
          [class.delivery-note-skipped]="hasSkippedDeliveries && !hasFailedDeliveries"
          [class.delivery-note-error]="hasFailedDeliveries"
          >
          <mat-icon>{{ hasFailedDeliveries ? 'error' : hasSkippedDeliveries ? 'warning' : 'check_circle' }}</mat-icon>
          <div>
            <span>
              {{ lastDeliverySummary.recipientCount }} resolved,
              {{ lastDeliverySummary.sentCount }} sent,
              {{ lastDeliverySummary.skippedCount }} skipped,
              {{ lastDeliverySummary.failureCount }} failed.
            </span>
            @if (hasDeliveryIssues) {
              <div class="delivery-issues">
                @if (skippedDeliveryIssues.length) {
                  <div>
                    <strong>Skipped</strong>
                    <ul>
                      <!-- Derived read-only results may omit IDs or repeat recipients. -->
                      @for (issue of skippedDeliveryIssues; track $index) {
                        <li>
                          <span>{{ getDeliveryIssueUser(issue) }}</span>
                          <small>{{ getDeliveryIssueReason(issue) }}</small>
                        </li>
                      }
                    </ul>
                  </div>
                }
                @if (failureDeliveryIssues.length) {
                  <div>
                    <strong>Failed</strong>
                    <ul>
                      @for (issue of failureDeliveryIssues; track $index) {
                        <li>
                          <span>{{ getDeliveryIssueUser(issue) }}</span>
                          <small>{{ getDeliveryIssueReason(issue) }}</small>
                        </li>
                      }
                    </ul>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
  </section>

  <section class="panel message-panel">
    <div class="section-title">
      <mat-icon>edit_notifications</mat-icon>
      <div>
        <h3>Message</h3>
        <p>Keep titles short and messages direct.</p>
      </div>
    </div>

    <div class="field-row">
      <mat-form-field appearance="fill" floatLabel="always">
        <mat-label>Title</mat-label>
        <input matInput formControlName="title">
        <mat-hint align="end">{{ titleLength }}/80</mat-hint>
      </mat-form-field>
    </div>

    <mat-form-field appearance="fill" floatLabel="always">
      <mat-label>Message</mat-label>
      <textarea matInput rows="5" formControlName="message"></textarea>
      <mat-hint align="end">{{ messageLength }}/240</mat-hint>
    </mat-form-field>
  </section>

  <aside class="panel preview-panel">
    <div class="section-title">
      <mat-icon>phone_iphone</mat-icon>
      <div>
        <h3>Preview</h3>
      </div>
    </div>

    <div class="phone-preview">
      <div class="notification-preview">
        <div class="app-icon">
          <mat-icon>notifications</mat-icon>
        </div>
        <div class="preview-copy">
          <strong>{{ form.get('title')?.value || 'Push title' }}</strong>
          <span>{{ form.get('message')?.value || 'Message body will appear here.' }}</span>
        </div>
      </div>
    </div>

    <div class="summary-list">
      <div>
        <span class="summary-label">Destination</span>
        <strong>{{ selectedDestinationLabel }}</strong>
      </div>
    </div>

    <button mat-raised-button color="primary" type="button" [disabled]="!canSend" (click)="send()">
      <mat-icon>send</mat-icon>
      Send Push
    </button>
  </aside>
</form>
</div>
